// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useEffect } from 'react';

import { type AutodeskUrn, type SaveAutodeskElementTagsRequest, useEvolveGet, useEvolvePost } from '@evolvemep/api-ts';

import { urlEncodeUrn } from 'components/Autodesk/extensions/utils';
import {
  useAutodeskExtensionStoreActions,
  useAutodeskExtensionStoreSnapshot,
} from 'components/Autodesk/useAutodeskExtensionStore';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { useBlockerWhen } from 'hooks/navigation/useBlockerWhen';

/**
 * Registers a persistence handler for the element tags extension.
 * @param cloudModelUrn The URN of the cloud model.
 */
export const useElementTagsPersistence = (cloudModelUrn: AutodeskUrn | null) => {
  const { registerPersistence, unregisterPersistence } = useAutodeskExtensionStoreActions('elementTags');
  const { isDirty } = useAutodeskExtensionStoreSnapshot('elementTags');
  const { externalIdLookup } = useAutodeskExtensionStoreSnapshot('apsViewer');
  const { load: loadTags } = useAutodeskExtensionStoreActions('elementTags');

  const { data: apiTags, setDefaultOpts } = useEvolveGet(
    `shop/autodesk/elementTags/${isNotNil(cloudModelUrn) ? urlEncodeUrn(cloudModelUrn) : ':urn'}`,
  );
  const { apiCall: saveElementTags } = useEvolvePost('shop/autodesk/elementTags/:urn');

  useBlockerWhen('ElementTagChanges', isDirty);

  // Load the model's tags whenever the model changes.
  useEffect(() => {
    setDefaultOpts({ lazy: isNil(cloudModelUrn) });
  }, [cloudModelUrn, setDefaultOpts]);

  // Register the batch-save persistence handler so the store can orchestrate viewer render + backend writes.
  const saveAll = useCallback(
    (request: SaveAutodeskElementTagsRequest) => {
      if (isNil(cloudModelUrn)) throw new Error('Cannot save element tags without a loaded model.');
      return saveElementTags(request, { url: `shop/autodesk/elementTags/${urlEncodeUrn(cloudModelUrn)}` });
    },
    [cloudModelUrn, saveElementTags],
  );

  useEffect(() => {
    registerPersistence({ saveAll });
    return unregisterPersistence;
  }, [registerPersistence, unregisterPersistence, saveAll]);

  // Render the loaded tags once the extension's viewer commands and the id lookup are ready.
  useEffect(() => {
    if (isNil(externalIdLookup) || isNil(apiTags)) return;
    loadTags(apiTags);
  }, [loadTags, externalIdLookup, apiTags]);
};
